import React, { type FC, type ReactNode, useEffect, useRef, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { variant, theme } from '@lucid/theme';
import { AccordionItemPanel } from 'react-accessible-accordion';
import gsap from 'gsap';

import type { Lucid } from '../../types/types';
import Box from '../../components/box/box';

export interface AccordionPanelProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The children to be rendered with the Accordion.Panel. */
  children?: ReactNode;
  /** The available variants for the `Accordion.Panel` component. */
  variant?: Lucid.Theme.Component.Accordion.Panel.VariantKeys;
  /** The state of the accordion */
  state?: Partial<{ expanded: boolean; disabled: boolean }>;
}

const StyledAccordionPanel = styled(AccordionItemPanel)<AccordionPanelProps>(
  variant({
    scale: 'accordion.panel',
  }),
  {
    label: 'panel',
  },
);

const AccordionPanel: FC<PropsWithChildren<AccordionPanelProps>> = ({
  className,
  variant = 'default',
  state,
  ...rest
}) => {
  const animationRef = useRef<HTMLDivElement>(null);
  const openAnimation = useRef<any>();
  const closeAnimation = useRef<any>();

  useEffect(() => {
    if (animationRef.current) {
      openAnimation.current = gsap
        .timeline({
          paused: true,
          defaults: {
            ease: theme.beziers.slowStartEase,
          },
        })
        .set(animationRef.current, { display: 'block' })
        .to(animationRef.current, {
          height: 'auto',
          duration: 0.2,
          delay: 0.05,
        })
        .to(animationRef.current, { y: 0, opacity: 1, duration: 0.12 }, '-=0.1');

      closeAnimation.current = gsap
        .timeline({
          paused: true,
          defaults: {
            ease: theme.beziers.slowStartEase,
          },
        })
        .to(animationRef.current, { opacity: 0, duration: 0.1 }, 0)
        .to(animationRef.current, { y: '-40px', duration: 0.15 }, 0)
        .to(animationRef.current, { height: 0, duration: 0.25 }, 0.08)
        .set(animationRef.current, { display: 'none' });
    }
  });

  const isExpanded = !!state?.expanded;

  useEffect(() => {
    if (animationRef.current && openAnimation.current && closeAnimation.current) {
      isExpanded ? openAnimation.current.play() : closeAnimation.current.play();
    }
  }, [isExpanded]);

  return (
    <Box ref={animationRef} css={{ opacity: 0, height: 0, width: '100%' }}>
      <StyledAccordionPanel
        {...rest}
        css={{ background: 'transparent', display: 'block' }}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
        variant={variant}
      />
    </Box>
  );
};

export { AccordionPanel };
export default AccordionPanel;
